import React, { useState, useEffect } from 'react';
import {
    LowerWrapper,
    PrinterPanelInputWrapper,
    HeadingWrapper,
    TextWrapper,
    HeadingTextWrapper,
    CancelSaveRightContentWrapper,
    StyledButton,
} from './PrinterPanel.styles';
import { SelectOption } from '../../types/ViewModel/SelectOption';
import PrintDropdown from './PrintDropdown';
import { useRotationStore } from '../../context/RotationContext';
import useRotation from '../../app/useRotation';
import { ExcelReportInfoBM } from '../../types/BusinessModel/ExcelReportInfoBM';
import { CargoReportEnum } from '../../enums/cargoReportEnum';
import ExcelReportInfoFactory from '../../factory/ExcelReportInfoFactory';
import usePostCreateExcel from '../../api/usePostCreateExcel';
import { useVesselStore } from '../../context/VesselContext';
import { useRef } from 'react';
import BerthCallInfoFactory from '../../factory/BerthCallInfoFactory';
import { BerthCallInfo } from '../../types/ViewModel/BerthCallInfo';
import { generateReport } from 'src/utils/reportGenerator/reportGenerator';
import { CargoEventReportData, REPORT_TYPE } from 'src/utils/reportGenerator/ReportGeneratorTypes';
import { useStowageStore } from 'src/context/StowageContext';
import { useEventStore } from 'src/context/EventContext';
import { mapPortCallToRotationPortCallSimple } from 'src/utils/stowage/integrations/rotation/portCallUtils';
import { useEventTypeStore } from 'src/context/EventTypeContext';
import { prettifyDateTime } from 'odfjell-date-time-utils/dateTimeFormatter';
import { getDateNow } from 'odfjell-date-time-utils/dateTimeUtils';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import useAppFeature from 'src/app/useAppFeature';

type Props = {
    onCancel: () => void;
    onPrint: () => void;
    isOpen: boolean;
};

const PrinterPanelComponent: React.FC<Props> = (props) => {
    const [cargoReportTypeOptions, setCargoReportTypeOptions] = useState<SelectOption[]>([]);
    const [berthCallOptions, setBerthCallOptions] = useState<SelectOption[]>([]);
    const [chartererOptions, setChartererOptions] = useState<SelectOption[]>([]);
    const [cargoOpsOptions, setCargoOpsOptions] = useState<SelectOption[]>([]);
    const [selectedCargoReportType, setSelectedCargoReportType] = useState<CargoReportEnum | null>(null);
    const [berthCallSelectList, setBerthCallSelectList] = useState<SelectOption[]>([]);
    const [chartererSelectList, setChartererSelectList] = useState<SelectOption[]>([]);
    const [cargoOpsSelectList, setCargoOpsSelectList] = useState<SelectOption[]>([]);
    const { selectedNode, rotation } = useRotationStore();
    const { selectedVessel } = useVesselStore();
    const { loadedMasterStowagePlan } = useStowageStore();
    const { createExcelReportInfoBM } = ExcelReportInfoFactory();
    const { createExcel } = usePostCreateExcel();
    const { getPortCallFromNode, getBerthCallFromNode } = useRotation();
    const { createBerthCallInfoListBM, createBerthCallInfoItem } = BerthCallInfoFactory();
    const { fullEventArray, deltaEventArray } = useEventStore();
    const { eventTypeData } = useEventTypeStore();
    const { isFeatureEnabled } = useAppFeature();

    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        const checkIfClickedOutside = (event) => {
            if (props.isOpen && ref.current && !ref.current.contains(event.target)) {
                props.onCancel();
            }
        };
        document.addEventListener('mousedown', checkIfClickedOutside);
        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [props.isOpen]);

    useEffect(() => {
        getReportTypeOptions();
        getBerthCallOptions();
    }, []);

    const getReportTypeOptions = () => {
        const optList: SelectOption[] = [];
        optList.push(new SelectOption(CargoReportEnum.CargoEvent.toString(), 'Cargo Report'));
        optList.push(new SelectOption(CargoReportEnum.CargoEventWithLaytime.toString(), 'Cargo Report With Laytime Odfjell internal report'));
        setCargoReportTypeOptions(optList);
    };

    const getBerthCallOptions = () => {
        if (selectedNode) {
            const pc = getPortCallFromNode(selectedNode);
            if (pc) {
                setBerthCallOptions(pc.berthCallArray.map((o) => new SelectOption(o.berthCallId?.toString(), o.name)));
                return;
            }
        }
        setBerthCallOptions([]);
    };

    const getChartererOptions = (selection: any) => {
        if (selectedNode) {
            const pc = getPortCallFromNode(selectedNode);
            if (pc) {
                const bcList = pc.berthCallArray.filter((bc) => {
                    if (selection.find((o) => o.value === bc.berthCallId?.toString())) {
                        return true;
                    }
                    return false;
                });
                const coList = bcList.flatMap((o) => o.cargoOperationArray);
                if (coList && coList.length > 0) {
                    const chList = coList.map((o) => o.chartererName);
                    const chListUnique = chList.filter(function (v, i) {
                        return chList.indexOf(v) == i;
                    });
                    let i = 0;
                    setChartererOptions(chListUnique.map((o) => new SelectOption((i++).toString(), o)));
                    return;
                }
            }
        }
        setChartererOptions([]);
    };

    const getCargoOpOptions = (selection: any) => {
        if (selectedNode) {
            const pc = getPortCallFromNode(selectedNode);
            if (pc) {
                const coList = pc.berthCallArray.flatMap((o) => o.cargoOperationArray);
                if (coList && coList.length > 0) {
                    const coListForCharter = coList.filter((co) => {
                        if (selection.find((o) => o.label === co.chartererName)) {
                            return true;
                        }
                        return false;
                    });
                    setCargoOpsOptions(coListForCharter.map((o) => new SelectOption(o.cargoOperationId?.toString(), o.name)));
                    return;
                }
            }
        }
        setCargoOpsOptions([]);
    };

    const cargoReportDropdownChange = (selection: any) => {
        const val: CargoReportEnum = +selection.value;
        setSelectedCargoReportType(val);
    };

    const berthDropdownChange = (selection: any) => {
        getChartererOptions(selection);
        setBerthCallSelectList(selection);
    };

    const chartererDropdownChange = (selection: any) => {
        getCargoOpOptions(selection);
        setChartererSelectList(selection);
    };

    const cargoDropdownChange = (selection: any) => {
        setCargoOpsSelectList(selection);
    };

    const cancelBtn = () => {
        props.onCancel();
    };

    const getValueListFromSelOptions = (selList: SelectOption[]): string | null => {
        if (selList.length > 0) {
            let str = '';
            let i = 1;
            selList.forEach((o) => {
                str += o.value;
                if (i++ < selList.length) {
                    str += ',';
                }
            });
            return str;
        }
        return null;
    };

    const getLabelListFromSelOptions = (selList: SelectOption[]): string | null => {
        if (selList.length > 0) {
            let str = '';
            let i = 1;
            selList.forEach((o) => {
                str += o.label;
                if (i++ < selList.length) {
                    str += ';';
                }
            });
            return str;
        }
        return null;
    };

    const getSelectedBerthCallInfoList = (selOptList: SelectOption[]): BerthCallInfo[] => {
        return selOptList.map((o) => {
            return createBerthCallInfoItem(+o.value, o.label);
        });
    };

    const getTotalBerthCallInfoList = (selOptList: SelectOption[]): BerthCallInfo[] => {
        return selOptList.map((o) => {
            return createBerthCallInfoItem(+o.value, o.label);
        });
    };

    const getExcelEventType = (repType: CargoReportEnum): ExcelReportInfoBM | null => {
        const pc = getPortCallFromNode(selectedNode);
        if (pc && pc.port) {
            const bc = getBerthCallFromNode(selectedNode);
            return createExcelReportInfoBM(
                repType,
                selectedVessel?.imoNumber,
                getPortCallFromNode(selectedNode)?.portCallId,
                pc.isInPortCluster,
                createBerthCallInfoListBM(getSelectedBerthCallInfoList(berthCallSelectList)),
                createBerthCallInfoListBM(getTotalBerthCallInfoList(berthCallOptions)),
                getLabelListFromSelOptions(chartererSelectList),
                getValueListFromSelOptions(cargoOpsSelectList),
                null,
                repType === CargoReportEnum.CargoEvent ? 'Cargo Events' : 'Cargo Events With Laytime',
                pc.port.portId,
                bc ? bc.berthId : null,
                null
            );
        }
        return null;
    };

    const printBtn = async () => {
        const excelReportInfo = getExcelEventType(selectedCargoReportType);
        if (
            isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber: selectedVessel.imoNumber, date: getDateNow() }) &&
            loadedMasterStowagePlan
        ) {
            const pc = getPortCallFromNode(selectedNode);
            if (!pc || !excelReportInfo) return;
            const reportData: CargoEventReportData = {
                stowagePlan: loadedMasterStowagePlan,
                vessel: selectedVessel,
                tankersEvents: fullEventArray,
                eventTypeData: eventTypeData,
                portCallId: pc.portCallId,
                reportType: REPORT_TYPE.CARGO_EVENT,
                selectedPortCall: mapPortCallToRotationPortCallSimple(pc, selectedVessel.imoNumber),
                excelReportInfo: excelReportInfo,
            };
            const result = await generateReport([{ data: reportData, reportType: REPORT_TYPE.CARGO_EVENT }]);

            const fileName = `${excelReportInfo.filename} ${prettifyDateTime(new Date())}.xlsx`;
            if (result.success) {
                try {
                    const _downloadLink = document.createElement('a');
                    _downloadLink.href = result.dataUri;
                    _downloadLink.download = fileName;
                    _downloadLink.style.display = 'none';
                    document.body.appendChild(_downloadLink);
                    _downloadLink.click();
                } catch (error) {
                    console.error('Error generating report:', error);
                }
            }
        } else {
            createExcel(getExcelEventType(selectedCargoReportType));
            props.onPrint();
        }
    };

    const isPrintDisabled = (): boolean => {
        return (
            selectedVessel?.hasOrcaWebSimpleMode ||
            ((!selectedCargoReportType || berthCallSelectList.length === 0 || chartererSelectList.length === 0 || cargoOpsSelectList.length === 0) &&
                berthCallOptions.length > 0)
        );
    };

    return (
        <>
            {props.isOpen && (
                <LowerWrapper ref={ref}>
                    <HeadingWrapper>
                        <HeadingTextWrapper>
                            {selectedCargoReportType === CargoReportEnum.CargoEventWithLaytime && <h4>Do not share outside Odfjell</h4>}
                            <h4>Cargo events report</h4>
                        </HeadingTextWrapper>
                    </HeadingWrapper>
                    <PrinterPanelInputWrapper>
                        {berthCallOptions.length === 0 && <TextWrapper>Print port log events?</TextWrapper>}
                        {cargoReportTypeOptions.length > 0 && (
                            <PrintDropdown
                                heading="Report type:"
                                multiple={false}
                                options={cargoReportTypeOptions}
                                onChange={cargoReportDropdownChange}
                            />
                        )}
                        {berthCallOptions.length > 0 && (
                            <PrintDropdown heading="Berth:" multiple={true} options={berthCallOptions} onChange={berthDropdownChange} />
                        )}
                        {berthCallSelectList.length > 0 && (
                            <PrintDropdown heading="Charterer:" multiple={true} options={chartererOptions} onChange={chartererDropdownChange} />
                        )}
                        {chartererSelectList.length > 0 && (
                            <PrintDropdown heading="Cargo:" multiple={true} options={cargoOpsOptions} onChange={cargoDropdownChange} />
                        )}
                        <CancelSaveRightContentWrapper>
                            <StyledButton className="cancel-btn" disabled={false} isSelected={true} onClick={cancelBtn}>
                                Cancel
                            </StyledButton>
                            <StyledButton className="primary-btn" disabled={isPrintDisabled()} isSelected={true} onClick={printBtn}>
                                Print
                            </StyledButton>
                        </CancelSaveRightContentWrapper>
                    </PrinterPanelInputWrapper>
                </LowerWrapper>
            )}
        </>
    );
};

export default PrinterPanelComponent;
